import { normalizeDepartmentId } from '../utils/normalizeIds';

// Role IDs that must not be used as department IDs (canonical + legacy)
const INVALID_DEPARTMENT_TOKENS = new Set([
  'SN', 'HWN',
  'SUPERVISOR_NURSE', 'HEAD_WARD_NURSE',
]);

const extractDocIdFromRef = (ref: unknown): string | null => {
  if (!ref) return null;
  if (typeof ref === 'string') {
    const id = ref.split('/').pop()?.trim();
    return id && !INVALID_DEPARTMENT_TOKENS.has(normalizeDepartmentId(id)) ? normalizeDepartmentId(id) : null;
  }
  if (typeof ref === 'object' && ref !== null) {
    const o = ref as { id?: unknown; path?: string };
    const id = typeof o.id === 'string' ? o.id.trim() : o.path?.split('/').pop()?.trim();
    return id && !INVALID_DEPARTMENT_TOKENS.has(normalizeDepartmentId(id)) ? normalizeDepartmentId(id) : null;
  }
  return null;
};

const toNormalizedDepartmentId = (value: unknown): string | null => {
  const normalized = normalizeDepartmentId(value);
  if (!normalized) return null;
  if (INVALID_DEPARTMENT_TOKENS.has(normalized)) return null;
  return normalized;
};

/**
 * Returns the departments explicitly assigned to the signed-in user (not LOF-expanded scope).
 * Prefers multi-department sources (departmentRoleById, departmentIds) over single departmentId.
 *
 * Priority: 1) departmentRoleById 2) departmentIds 3) roles.head_ward_nurse_of 4) departmentId/Ref/department (fallback)
 */
export const getUserAssignedDepartmentIds = (
  firestoreUser: Record<string, unknown> | null | undefined,
): string[] => {
  if (!firestoreUser) return [];

  const seen = new Set<string>();
  const push = (value: unknown) => {
    const normalized = toNormalizedDepartmentId(value);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
  };
  const pushRef = (ref: unknown) => {
    const id = extractDocIdFromRef(ref);
    if (id && !seen.has(id)) seen.add(id);
  };

  // 1. Prefer departmentRoleById (multi-dept with roles)
  const deptRoleById = (firestoreUser as { departmentRoleById?: unknown }).departmentRoleById;
  if (deptRoleById && typeof deptRoleById === 'object' && !Array.isArray(deptRoleById)) {
    Object.keys(deptRoleById as Record<string, unknown>).forEach(push);
  }

  // 2. Prefer departmentIds (multi-dept array)
  const deptIds = (firestoreUser as { departmentIds?: unknown }).departmentIds;
  if (Array.isArray(deptIds)) {
    deptIds.forEach(push);
  }

  // 3. roles.head_ward_nurse_of
  const roles = (firestoreUser as { roles?: Record<string, unknown> }).roles;
  const headWardNurseOf = roles && Array.isArray((roles as { head_ward_nurse_of?: unknown }).head_ward_nurse_of)
    ? (roles as { head_ward_nurse_of: string[] }).head_ward_nurse_of
    : [];
  headWardNurseOf.forEach((id) => {
    if (typeof id === 'string' && id.trim()) push(id.trim());
  });

  // 4. Fallback: single departmentId, departmentRef, department
  const deptId = (firestoreUser as { departmentId?: unknown }).departmentId;
  if (typeof deptId === 'string') {
    push(deptId);
  } else {
    pushRef(deptId);
  }
  pushRef((firestoreUser as { departmentRef?: unknown }).departmentRef);
  const dept = (firestoreUser as { department?: unknown }).department;
  if (Array.isArray(dept) && dept.length > 0) {
    pushRef(dept[0]);
  } else {
    pushRef(dept);
  }

  return Array.from(seen);
};

